import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import momentTz from 'moment-timezone';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';
import {
  getRandomColor,
  mapAlertResponseToCloneFormSteps,
  mapAlertResponseToFormSteps,
  mapEditFormStateToJson,
  mapStateToCreateAlertJson,
} from '../../../components/xlalerts/alertforms/AlertFormUtility';
import { parseChannelKey } from '../../../utility/channelUtils';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import { selectSessionInformation } from '../sessionSlice';

const AlertFormSteps = {
  SELECT_SENSORS: 'SELECT_SENSORS',
  DEFINE_THRESHOLD: 'DEFINE_THRESHOLD',
  DEFINE_ACTIONS: 'DEFINE_ACTIONS',
  NAME_ALERT: 'NAME_ALERT',
  REVIEW: 'REVIEW',
};

export const ALERT_FORM_MAX_GRAPHED_CHANNELS = 10;

// Selectors
export const selectAlertFormState = (state) => state.alertForm;

export const selectIsEditingAlertForm = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm.isEditing,
);

export const selectEditingAlertId = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm.editingAlertId,
);

export const selectSensorsData = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm[AlertFormSteps.SELECT_SENSORS],
);

export const selectThresholdData = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm[AlertFormSteps.DEFINE_THRESHOLD],
);
export const selectActionsData = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm[AlertFormSteps.DEFINE_ACTIONS],
);

export const selectNameAlertData = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm[AlertFormSteps.NAME_ALERT],
);

export const selectReviewData = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm[AlertFormSteps.REVIEW],
);

const selectAlertFormStep = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm.currentFormStep,
);

const selectAlertFormIsValid = createSelector(
  selectAlertFormState,
  (alertForm) => alertForm.isFormDataValid,
);

export const selectThresholdValue = createSelector(
  selectThresholdData,
  (thresholdData) => thresholdData.thresholdValue,
);

export const selectThresholdOperator = createSelector(
  selectThresholdData,
  (thresholdData) => thresholdData.thresholdOperator,
);
export const selectEditAlertLoadStatus = (state) => state.alertForm.editLoadStatus;

export const selectEditAlertIsLoaded = (state) => state
  .alertForm.editLoadStatus === CrudStatus.SUCCESS;

const addToGraphedList = (sensorState, newChannelIds) => {
  if (sensorState.graphedChannels.length === 0) {
    const { channelGroupType, channelIndex, channelType } = parseChannelKey(newChannelIds[0]);
    sensorState.graphedAlertConfig.channelKeyObject = {
      channelGroupType,
      channelIndex,
      channelType,
    };
  }
  newChannelIds.forEach((newChannelId) => {
    if (sensorState.graphedChannels.length
      < ALERT_FORM_MAX_GRAPHED_CHANNELS) {
      if (!sensorState.graphedChannels.includes(newChannelId)) {
        sensorState.graphedChannels.push(newChannelId);
        sensorState.graphedChannelColors[newChannelId] = getRandomColor();
      }
    }
  });
};

const removeFromGraphedList = (sensorState, channelIdsToRemove) => {
  channelIdsToRemove.forEach((channelIdToRemove) => {
    const graphedIndex = sensorState.graphedChannels.indexOf(
      channelIdToRemove,
    );
    if (graphedIndex > -1) {
      sensorState.graphedChannels.splice(graphedIndex, 1);
      delete sensorState.graphedChannelColors[channelIdToRemove];
    }
  });
};

export const createAlerts = createAsyncThunk(
  'alertForm/createAlerts',
  async (_, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const sessionInfo = selectSessionInformation(state);
      const payload = mapStateToCreateAlertJson(
        selectSensorsData(state),
        selectThresholdData(state),
        selectActionsData(state),
        selectNameAlertData(state),
        sessionInfo,
      );

      return await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${window.envConfig.apiUrl}/api/v1/alerts/`, { data: payload });
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

export const updateAlert = createAsyncThunk(
  'alertForm/updateAlert',
  async ({ alertId }, thunkAPI) => {
    try {
      const state = thunkAPI.getState();
      const sessionInfo = selectSessionInformation(state);
      const payload = mapEditFormStateToJson(
        selectSensorsData(state),
        selectThresholdData(state),
        selectActionsData(state),
        selectNameAlertData(state),
        sessionInfo,
      );
      const patchPayload = {
        ...payload,
      };
      return await RaaAuthenticatedRemoteService
        .globalInstance()
        .post(
          `${window.envConfig.apiUrl}/api/v1/alerts/${alertId}`,
          { data: patchPayload },
        );
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
        data: error.data || [],
      });
    }
  },
);

export const loadAlertForEdit = createAsyncThunk(
  'alertForm/loadAlertForEdit',
  async ({ alertId }, thunkAPI) => {
    try {
      const sessionInfo = selectSessionInformation(thunkAPI.getState());
      const response = await RaaAuthenticatedRemoteService
        .globalInstance()
        .get(`${window.envConfig.apiUrl}/api/v1/alerts/data/${alertId}`);

      return { response, sessionInfo };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
      });
    }
  },
);

export const loadAlertForClone = createAsyncThunk(
  'alertForm/loadAlertForClone',
  async ({ alertId }, thunkAPI) => {
    try {
      const sessionInfo = selectSessionInformation(thunkAPI.getState());
      const response = await RaaAuthenticatedRemoteService
        .globalInstance()
        .get(`${window.envConfig.apiUrl}/api/v1/alerts/data/${alertId}`);

      return { response, sessionInfo };
    } catch (error) {
      return thunkAPI.rejectWithValue({
        name: error.name,
        message: error.message,
      });
    }
  },
);

const initialState = {
  currentFormStep: AlertFormSteps.SELECT_SENSORS,
  isFormDataValid: true,
  isEditing: false,
  editingAlertId: null,
  editLoadStatus: CrudStatus.IDLE,
  editLoadError: null,
  [AlertFormSteps.SELECT_SENSORS]: {
    selectedChannels: [],
    graphedChannels: [],
    graphedChannelColors: {},
    removedChannelKey: null,
    completed: false,
    readingType: 0,
    initialReadingType: 0,
    channelType: 0,
    graphedAlertConfig: {
      threshold: null,
      channelKeyObject: {
        channelGroupType: null,
        channelIndex: null,
        channelType: null,
      },
      customUnits: '',
    },
    errorList: [],
  },
  [AlertFormSteps.DEFINE_THRESHOLD]: {
    useSharedThreshold: false,
    thresholdId: -1,
    thresholdValue: null,
    thresholdOperator: null,
    completed: false,
    errorList: [{ field: 'startThresholdValue', message: 'Threshold value is required' }],
  },
  [AlertFormSteps.DEFINE_ACTIONS]: {
    actions: [],
    completed: false,
    errorList: [],
  },
  [AlertFormSteps.NAME_ALERT]: {
    alertNameInput: '',
    alertNamePairs: [],
    duplicateNamesDetected: false,
    alertTags: [],
    additionalSensors: [],
    completed: false,
    timeZoneName: momentTz.tz.guess(),
    errorList: [],
  },
  [AlertFormSteps.REVIEW]: {
    completed: false,
    unexpectedError: false,
    failedChannelKeys: [],
    errorData: [],
    isSubmitting: false,
  },
};

export const alertFormSlice = createSlice({
  name: 'alertForm',
  initialState,
  reducers: {
    setStepErrorList: (state, action) => {
      const { step, errorList } = action.payload;
      if (state[step]) {
        state[step].errorList = errorList;
      }
    },
    setStepCompleted: (state, action) => {
      const { step, completed } = action.payload;
      if (state[step]) {
        state[step].completed = completed;
      }
    },
    updateStepData: (state, action) => {
      const { step, data } = action.payload;
      state[step] = {
        ...state[step],
        ...data,
        errorList: [],
      };
    },
    clearStepData: (state, action) => {
      const { step } = action.payload;
      state[step] = initialState[step];
    },
    addChannelIdsToSelectedList: (state, action) => {
      const { data } = action.payload;
      const { newChannelIds } = data;

      const sensorState = state[AlertFormSteps.SELECT_SENSORS];

      newChannelIds.forEach((newChannelId) => {
        if (!sensorState.selectedChannels.includes(newChannelId)) {
          sensorState.selectedChannels.push(newChannelId);
        }
      });

      // Call the graphed list logic
      addToGraphedList(sensorState, newChannelIds);
    },
    removeChannelIdsFromSelectedList: (state, action) => {
      const { data } = action.payload;
      const { channelIdsToRemove } = data;

      const sensorState = state[AlertFormSteps.SELECT_SENSORS];

      channelIdsToRemove.forEach((channelIdToRemove) => {
        const selectedIndex = sensorState.selectedChannels.indexOf(
          channelIdToRemove,
        );
        if (selectedIndex > -1) {
          sensorState.selectedChannels.splice(selectedIndex, 1);
        }
      });

      removeFromGraphedList(sensorState, channelIdsToRemove);
    },
    addChannelIdsToGraphedList: (state, action) => {
      const { data } = action.payload;
      const { newChannelIds } = data;

      const sensorState = state[AlertFormSteps.SELECT_SENSORS];
      addToGraphedList(sensorState, newChannelIds);
    },
    removeChannelIdsFromGraphedList: (state, action) => {
      const { data } = action.payload;
      const { channelIdsToRemove } = data;

      const sensorState = state[AlertFormSteps.SELECT_SENSORS];
      removeFromGraphedList(sensorState, channelIdsToRemove);
    },
    addNotificationAction: (state, action) => {
      const { data } = action.payload;
      const { newAction } = data;
      const actionState = state[AlertFormSteps.DEFINE_ACTIONS];
      actionState.actions.push(newAction);
    },
    removeNotificationAction: (state, action) => {
      const { id } = action.payload;
      const actionState = state[AlertFormSteps.DEFINE_ACTIONS];
      actionState.actions = actionState.actions.filter((item) => item.id !== id);
    },
    updateNotificationAction: (state, action) => {
      const { updatedAction } = action.payload;
      const actionState = state[AlertFormSteps.DEFINE_ACTIONS];
      const index = actionState.actions.findIndex(
        (a) => a.id === updatedAction.id,
      );
      if (index !== -1) {
        actionState.actions[index] = {
          ...actionState.actions[index],
          ...updatedAction,
        };
      }
    },
    setSelectedReadingType: (state, action) => {
      const { readingType } = action.payload;
      const sensorState = state[AlertFormSteps.SELECT_SENSORS];
      sensorState.readingType = readingType;
      if (state.isEditing) {
        if (sensorState.initialReadingType !== null
          && readingType !== sensorState.initialReadingType) {
          state[
            AlertFormSteps.DEFINE_THRESHOLD] = { ...initialState[AlertFormSteps.DEFINE_THRESHOLD] };
        }
      }
    },
    setSelectedChannelType: (state, action) => {
      const { channelType } = action.payload;
      const sensorState = state[AlertFormSteps.SELECT_SENSORS];
      sensorState.channelType = channelType;
    },
    setRemovedChannelKey: (state, action) => {
      const { channelKey } = action.payload;
      const sensorState = state[AlertFormSteps.SELECT_SENSORS];
      sensorState.removedChannelKey = channelKey;
    },
    updateAlertName: (state, action) => {
      const { alertNameInput, alertNamePairs } = action.payload;

      const pairs = Array.isArray(alertNamePairs) ? alertNamePairs : [];
      const names = pairs.map((p) => (p && p.alertName ? p.alertName : ''));

      const hasDuplicates = names.length > 1 && new Set(names).size < names.length;

      state[AlertFormSteps.NAME_ALERT] = {
        ...state[AlertFormSteps.NAME_ALERT],
        alertNameInput,
        alertNamePairs: pairs,
        duplicateNamesDetected: hasDuplicates,
      };
    },
    resetForm: () => initialState,
    setIsFormValid: (state, action) => {
      state.isFormDataValid = action.payload;
    },
    setAlertFormMode: (state, action) => {
      const { isEditing, alertId } = action.payload;
      state.isEditing = isEditing;
      state.editingAlertId = alertId || null;
    },
    restoreFormState: (state, action) => {
      const snapshot = action.payload;

      if (!snapshot || typeof snapshot !== 'object') {
        return state;
      }
      return snapshot;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(createAlerts.pending, (state) => {
        state[AlertFormSteps.REVIEW] = {
          ...state[AlertFormSteps.REVIEW],
          unexpectedError: false,
          isSubmitting: true,
        };
      })
      .addCase(createAlerts.fulfilled, (state, action) => {
        const response = action.payload || {};

        if (response.success === false) {
          const data = response.data || {};
          const channelsWithErrors = data.channelsWithErrors || [];

          const failedKeys = Array.isArray(channelsWithErrors)
            ? channelsWithErrors
            : [];

          state[AlertFormSteps.REVIEW] = {
            ...state[AlertFormSteps.REVIEW],
            completed: false,
            failedChannelKeys: failedKeys,
            unexpectedError: false,
            errorData: response.data,
            isSubmitting: false,
          };
        } else {
          state[AlertFormSteps.REVIEW] = {
            ...state[AlertFormSteps.REVIEW],
            completed: true,
            failedChannelKeys: [],
            unexpectedError: false,
            errorData: [],
            isSubmitting: false,
          };
        }
      })
      .addCase(createAlerts.rejected, (state, action) => {
        const errorData = action.payload && action.payload.data ? action.payload.data : [];
        const failedKeys = Array.isArray(errorData)
          ? errorData.map((err) => err.channelKey).filter(Boolean)
          : [];
        state[AlertFormSteps.REVIEW] = {
          completed: false,
          failedChannelKeys: failedKeys,
          unexpectedError: true,
          errorData,
          isSubmitting: false,
        };
      })
      .addCase(loadAlertForEdit.pending, (state) => {
        state.editLoadStatus = CrudStatus.PENDING;
        state.editLoadError = null;
      })
      .addCase(loadAlertForEdit.fulfilled, (state, action) => {
        const { response, sessionInfo } = action.payload;

        const mappedSteps = mapAlertResponseToFormSteps(response, sessionInfo);

        Object.keys(mappedSteps).forEach((step) => {
          state[step] = {
            ...state[step],
            ...mappedSteps[step],
          };
        });

        const sensorState = state[AlertFormSteps.SELECT_SENSORS];
        sensorState.initialReadingType = sensorState.readingType;

        state.isEditing = true;
        state.currentFormStep = AlertFormSteps.REVIEW;
        state.editLoadStatus = CrudStatus.SUCCESS;
      })
      .addCase(loadAlertForEdit.rejected, (state, action) => {
        state.editLoadStatus = CrudStatus.ERROR;
        state.editLoadError = action.payload;
      })
      .addCase(updateAlert.pending, (state) => {
        state[AlertFormSteps.REVIEW] = {
          ...state[AlertFormSteps.REVIEW],
          unexpectedError: false,
          isSubmitting: true,
        };
      })
      .addCase(updateAlert.fulfilled, (state, action) => {
        const response = action.payload || {};

        if (response.success === false) {
          const data = response.data || {};
          const channelsWithErrors = data.channelsWithErrors || [];

          const failedKeys = Array.isArray(channelsWithErrors)
            ? channelsWithErrors
            : [];

          state[AlertFormSteps.REVIEW] = {
            ...state[AlertFormSteps.REVIEW],
            completed: false,
            failedChannelKeys: failedKeys,
            unexpectedError: false,
            errorData: response.data,
            isSubmitting: false,
          };
        } else {
          state[AlertFormSteps.REVIEW] = {
            ...state[AlertFormSteps.REVIEW],
            completed: true,
            failedChannelKeys: [],
            unexpectedError: false,
            errorData: [],
            isSubmitting: false,
          };
        }
      })
      .addCase(updateAlert.rejected, (state, action) => {
        const errorData = action.payload && action.payload.data ? action.payload.data : [];

        const failedKeys = Array.isArray(errorData)
          ? errorData.map((err) => err.channelKey).filter(Boolean)
          : [];

        state[AlertFormSteps.REVIEW] = {
          completed: false,
          failedChannelKeys: failedKeys,
          unexpectedError: true,
          errorData,
          isSubmitting: false,
        };
      })
      .addCase(loadAlertForClone.pending, (state) => {
        state.editLoadStatus = CrudStatus.PENDING;
        state.editLoadError = null;
      })
      .addCase(loadAlertForClone.fulfilled, (state, action) => {
        const { response, sessionInfo } = action.payload || {};

        const mappedSteps = mapAlertResponseToCloneFormSteps(response, sessionInfo);

        Object.keys(mappedSteps).forEach((step) => {
          state[step] = {
            ...state[step],
            ...mappedSteps[step],
          };
        });

        // Clone = create mode
        state.isEditing = false;
        state.editingAlertId = null;
        state.currentFormStep = AlertFormSteps.REVIEW;
        state.isFormDataValid = true;

        state.editLoadStatus = CrudStatus.SUCCESS;
      })
      .addCase(loadAlertForClone.rejected, (state, action) => {
        state.editLoadStatus = CrudStatus.ERROR;
        state.editLoadError = action.payload;
      });
  },
});

export const {
  setStepErrorList,
  updateStepData,
  resetForm,
  clearStepData,
  addChannelIdsToSelectedList,
  removeChannelIdsFromSelectedList,
  addChannelIdsToGraphedList,
  removeChannelIdsFromGraphedList,
  setSelectedReadingType,
  setSelectedChannelType,
  addNotificationAction,
  removeNotificationAction,
  updateAlertName,
  setIsFormValid,
  setAlertFormMode,
  setRemovedChannelKey,
  setStepCompleted,
  restoreFormState,
} = alertFormSlice.actions;

export {
  AlertFormSteps, selectAlertFormStep, selectAlertFormIsValid,
};

export default alertFormSlice.reducer;
